import React, { useRef, useState } from 'react';

import { Box, Tooltip, useMediaQuery, useTheme } from '@material-ui/core';
import CheckCircleOutlinedIcon from '@material-ui/icons/CheckCircleOutlined';
import DeleteOutlinedIcon from '@material-ui/icons/DeleteOutlined';
import GetAppOutlinedIcon from '@material-ui/icons/GetAppOutlined';
import PeopleOutlinedIcon from '@material-ui/icons/PeopleOutlined';
import PrintOutlinedIcon from '@material-ui/icons/PrintOutlined';
import ReplayIcon from '@material-ui/icons/Replay';
import clsx from 'clsx';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';
import { useReactToPrint } from 'react-to-print';

import PrintOptionsDialog, { PrintOptions } from 'components/_dialogs/PrintOptionsDialog/PrintOptionsDialog';
import ActionLibraryInstancePrintTemplate, {
  PreFetchedPrintData,
} from 'components/_print/_templates/ActionLibraryInstancePrintTemplate/ActionLibraryInstancePrintTemplate';
import CenteredGrid from 'components/CenteredGrid/CenteredGrid';
import ColoredButton from 'components/ColoredButton';
import actionInstanceStepApi from 'config/api/actionInstanceStep/actionInstanceStep';
import actionLibraryInstancesApi from 'config/api/actionLibraryInstance/actionLibraryInstance';
import type { ActionLibraryInstance } from 'config/api/actionLibraryInstance/actionLibraryInstance.types';
import checkAccess from 'config/permissions/checkAccess';
import RolesOrganization from 'config/permissions/RolesOrganization';
import RolesPocketSafe from 'config/permissions/RolesPocketSafe';
import useAssetActionInstanceExport from 'hooks/useAssetActionInstanceExport/useAssetActionInstanceExport';
import action_library_messages from 'messages/action_library_messages';
import { useConfirmationModalContext } from 'reactContext/ConfirmationModalContext/ConfirmationModalContext';
import userDataStorage from 'storages/userDataStorage';

import DelegateInstanceDialog from '../DelegateInstanceDialog/DelegateInstanceDialog';

import useStyles from './ActionLibraryInstanceActions.styles';

type Props = {
  canComplete: boolean;
  canReopen?: boolean;
  completeDisabledTooltip: string;
  instance: ActionLibraryInstance;
  onCompleteClick: () => void;
  onDelete: () => void;
  onInstanceUpdate?: () => void;
  onReopenClick?: () => void;
};

const ActionLibraryInstanceActions: React.FC<Props> = ({
  canComplete,
  canReopen,
  completeDisabledTooltip,
  instance,
  onCompleteClick,
  onDelete,
  onInstanceUpdate,
  onReopenClick,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { showConfirmationModal } = useConfirmationModalContext();
  const queryClient = useQueryClient();
  const userData = userDataStorage.get() ?? {};
  const canExportInstanceJson =
    checkAccess(userData.ps_roles ?? [], [RolesPocketSafe.ActionLibraryAdministrator]) ||
    checkAccess(userData.roles ?? [], [RolesOrganization.AssetsManager]) ||
    Boolean(userData.is_asset_owner);
  const canDelegate = instance.canDelegate === true;
  const exportMutation = useAssetActionInstanceExport();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const styles = useStyles();
  const [delegateDialogOpen, setDelegateDialogOpen] = useState(false);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [printOptions, setPrintOptions] = useState<PrintOptions | undefined>(undefined);
  const [printData, setPrintData] = useState<PreFetchedPrintData | undefined>(undefined);
  const [isPrinting, setIsPrinting] = useState(false);
  const printComponentRef = useRef<HTMLDivElement>(null);

  const handlePrint = useReactToPrint({
    content: () => printComponentRef.current,
  });

  const handlePrintClick = () => {
    setPrintDialogOpen(true);
  };

  const handlePrintConfirm = async (options: PrintOptions) => {
    setPrintDialogOpen(false);

    const promises: Promise<unknown>[] = [];
    const needsInstanceComments = options.instanceComments;
    const needsInstanceLogs = options.instanceLogs;
    const needsStepComments = options.stepComments && (instance.steps?.length ?? 0) > 0;
    const needsStepLogs = options.stepActivityLogs && (instance.steps?.length ?? 0) > 0;

    if (needsInstanceComments) {
      promises.push(actionLibraryInstancesApi.getInstanceComments(instance.id));
    }
    if (needsInstanceLogs) {
      promises.push(actionLibraryInstancesApi.getInstanceStatusLogs(instance.id, { limit: 500 }));
    }
    if (needsStepComments && instance.steps) {
      instance.steps.forEach(step => {
        promises.push(actionInstanceStepApi.getComments(step.id));
      });
    }
    if (needsStepLogs && instance.steps) {
      instance.steps.forEach(step => {
        promises.push(actionInstanceStepApi.getStatusLogs(step.id, { limit: 500 }));
      });
    }

    try {
      const results = await Promise.all(promises);

      let instanceComments: PreFetchedPrintData['instanceComments'] = [];
      let instanceLogs: PreFetchedPrintData['instanceLogs'] = [];
      const stepComments: PreFetchedPrintData['stepComments'] = {};
      const stepLogs: PreFetchedPrintData['stepLogs'] = {};
      let resultIndex = 0;

      if (needsInstanceComments && results[resultIndex]) {
        const res = results[resultIndex] as { data: { results: PreFetchedPrintData['instanceComments'] } };
        instanceComments = res.data.results;
        resultIndex += 1;
      }
      if (needsInstanceLogs && results[resultIndex]) {
        const res = results[resultIndex] as { data: { results: PreFetchedPrintData['instanceLogs'] } };
        instanceLogs = res.data.results;
        resultIndex += 1;
      }
      if (needsStepComments && instance.steps) {
        instance.steps.forEach(step => {
          const res = results[resultIndex] as { data: { results: PreFetchedPrintData['stepComments'][number] } };
          stepComments[step.id] = res?.data?.results ?? [];
          resultIndex += 1;
        });
      }
      if (needsStepLogs && instance.steps) {
        instance.steps.forEach(step => {
          const res = results[resultIndex] as { data: { results: PreFetchedPrintData['stepLogs'][number] } };
          stepLogs[step.id] = res?.data?.results ?? [];
          resultIndex += 1;
        });
      }

      setPrintData({ instanceComments, instanceLogs, stepComments, stepLogs });
      setPrintOptions(options);
      setIsPrinting(true);
    } catch {
      enqueueSnackbar(t(action_library_messages.errors.something_went_wrong_try_later), { variant: 'error' });
    }
  };

  React.useEffect(() => {
    if (!isPrinting || !printOptions) return undefined;
    const timer = setTimeout(() => {
      handlePrint();
      setIsPrinting(false);
      setPrintOptions(undefined);
      setPrintData(undefined);
    }, 150);
    return () => clearTimeout(timer);
  }, [isPrinting, printOptions, handlePrint]);

  const canDelete = instance.canDelete === true;

  const { mutate: deleteInstance, isLoading: deleteLoading } = useMutation(actionLibraryInstancesApi.deleteActionLibraryInstance, {
    onSuccess: () => {
      queryClient.invalidateQueries('ACTION_LIBRARY_INSTANCES_GET_ALL');
      enqueueSnackbar(t(action_library_messages.success.instance_deleted), { variant: 'success' });
      onDelete();
    },
    onError: () => {
      enqueueSnackbar(t(action_library_messages.errors.instance_create_failed), { variant: 'error' });
    },
  });

  const handleDelete = async () => {
    const confirmed = await showConfirmationModal({
      title: t(action_library_messages.delete_confirmation.title),
      body: t(action_library_messages.delete_confirmation.body),
    });

    if (confirmed) {
      deleteInstance(instance.id);
    }
  };

  const handleDelegateSuccess = () => {
    onInstanceUpdate?.();
  };

  return (
    <CenteredGrid width='lg' withoutPadding>
      <Box className={clsx(styles.actionsRow, isMobile && styles.actionsRowMobile)}>
        {instance.status !== 'completed' &&
          (!canComplete ? (
            <Box className={styles.actionCell}>
              <Tooltip title={completeDisabledTooltip}>
                <Box className={styles.tooltipTrigger} component='span'>
                  <ColoredButton
                    className={styles.stretchButton}
                    customColor='secondary'
                    disabled
                    icon={false}
                    showLoader={false}
                    startIcon={<CheckCircleOutlinedIcon />}
                    textLike={false}
                    variant='outlined'
                  >
                    {t(action_library_messages.instance_actions.complete_instance)}
                  </ColoredButton>
                </Box>
              </Tooltip>
            </Box>
          ) : (
            <Box className={styles.actionCell}>
              <ColoredButton
                className={styles.stretchButton}
                customColor='secondary'
                disabled={false}
                icon={false}
                onClick={onCompleteClick}
                showLoader={false}
                startIcon={<CheckCircleOutlinedIcon />}
                textLike={false}
                variant='outlined'
              >
                {t(action_library_messages.instance_actions.complete_instance)}
              </ColoredButton>
            </Box>
          ))}
        {instance.status === 'completed' && canReopen && onReopenClick && (
          <Box className={styles.actionCell}>
            <ColoredButton
              className={styles.stretchButton}
              customColor='secondary'
              disabled={false}
              fullWidth={false}
              icon={false}
              onClick={onReopenClick}
              showLoader={false}
              startIcon={<ReplayIcon />}
              textLike={false}
              variant='outlined'
            >
              {t(action_library_messages.instance_actions.reopen_instance)}
            </ColoredButton>
          </Box>
        )}

        <Box className={styles.actionCell}>
          <ColoredButton
            className={styles.stretchButton}
            customColor='secondary'
            disabled={false}
            icon={false}
            onClick={handlePrintClick}
            showLoader={false}
            startIcon={<PrintOutlinedIcon />}
            textLike={false}
            variant='outlined'
          >
            {t(action_library_messages.actions.print)}
          </ColoredButton>
        </Box>
        {canExportInstanceJson && (
          <Box className={styles.actionCell}>
            <Tooltip title={t(action_library_messages.instance.export.export_json_tooltip)}>
              <Box className={styles.tooltipTrigger} component='span'>
                <ColoredButton
                  className={styles.stretchButton}
                  customColor='secondary'
                  disabled={exportMutation.isLoading}
                  icon={false}
                  onClick={() =>
                    exportMutation.mutate({
                      export_format: 'json',
                      id: instance.id,
                    })
                  }
                  showLoader={exportMutation.isLoading}
                  startIcon={<GetAppOutlinedIcon />}
                  textLike={false}
                  variant='outlined'
                >
                  {t(action_library_messages.instance.export.export_json)}
                </ColoredButton>
              </Box>
            </Tooltip>
          </Box>
        )}

        {canDelegate && (
          <Box className={styles.actionCell}>
            <ColoredButton
              className={styles.stretchButton}
              customColor='secondary'
              disabled={false}
              icon={false}
              onClick={() => setDelegateDialogOpen(true)}
              showLoader={false}
              startIcon={<PeopleOutlinedIcon />}
              textLike={false}
              variant='outlined'
            >
              {t(action_library_messages.instance_actions.delegate)}
            </ColoredButton>
          </Box>
        )}
        {canDelete && (
          <Box className={styles.actionCell}>
            <Tooltip title={instance.status === 'completed' ? t(action_library_messages.instance_actions.delete_completed_blocked) : ''}>
              <Box className={styles.tooltipTrigger} component='span'>
                <ColoredButton
                  className={styles.stretchButton}
                  customColor={instance.status === 'completed' ? 'secondary' : 'danger'}
                  disabled={deleteLoading || instance.status === 'completed'}
                  icon={false}
                  onClick={handleDelete}
                  showLoader={deleteLoading}
                  startIcon={<DeleteOutlinedIcon />}
                  textLike={false}
                  variant='outlined'
                >
                  {t(action_library_messages.actions.delete)}
                </ColoredButton>
              </Box>
            </Tooltip>
          </Box>
        )}
      </Box>
      {/* Hidden print template - only mount with options so template never receives undefined */}
      <div style={{ display: 'none' }}>
        <div ref={printComponentRef}>
          {printOptions ? (
            <ActionLibraryInstancePrintTemplate
              key={JSON.stringify(printOptions)}
              instance={instance}
              options={printOptions}
              preFetchedPrintData={printData}
            />
          ) : (
            <div />
          )}
        </div>
      </div>
      <PrintOptionsDialog onClose={() => setPrintDialogOpen(false)} onPrint={handlePrintConfirm} open={printDialogOpen} />

      <DelegateInstanceDialog
        instance={instance}
        onClose={() => setDelegateDialogOpen(false)}
        onSuccess={handleDelegateSuccess}
        open={delegateDialogOpen}
      />
    </CenteredGrid>
  );
};

export default ActionLibraryInstanceActions;
